import { Box, Button, Divider, Typography } from "@mui/material";
import type { ReactNode } from "react";

import AppDialog from "components/common/AppDialog";
import UserAvatar from "components/common/UserAvatar";
import {
  type SecureFile,
  useGetSecureFileDownloadsQuery,
  useGetSecureFileHistoryQuery,
} from "features/secureUploads/secureUploads";
import { useViewAs } from "hooks/useViewAs";

import { ConfidentialChip } from "./SecureFileTable";
import { dateTime, eventLine, fileSize } from "./fileModel";
import { locationLabel } from "./uploadModel";

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: "text.secondary",
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ fontSize: 14 }}>{children}</Box>
    </Box>
  );
}

/** What a file is, who it is for and who opened it; staff also read its history
 * (a move names projects a client user may not be on). */
export default function FileDetailsDialog({
  file,
  onClose,
}: {
  file: SecureFile | null;
  onClose: () => void;
}) {
  const { profile } = useViewAs();
  const staff = profile?.user_type === "internal";
  const { data: history = [] } = useGetSecureFileHistoryQuery(file?.id ?? 0, {
    skip: !file || !staff,
  });
  const { data: downloads = [] } = useGetSecureFileDownloadsQuery(
    file?.id ?? 0,
    { skip: !file },
  );
  if (!file) return null;

  return (
    <AppDialog
      open
      onClose={onClose}
      title="File Details"
      maxWidth="sm"
      actions={
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      }
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Typography sx={{ fontWeight: 700, wordBreak: "break-all" }}>
          {file.file_name}
        </Typography>
        {file.is_confidential ? <ConfidentialChip /> : null}
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        <Field label="Location">
          {locationLabel(file.project_name, file.collection_name)}
        </Field>
        <Field label="Size">{fileSize(file.size)}</Field>
        <Field label="Type">{file.content_type || "-"}</Field>
        <Field label="Added By">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {file.added_by ? (
              <UserAvatar person={file.added_by} size={22} />
            ) : null}
            <span>{file.added_by?.name ?? file.uploaded_by_name ?? "-"}</span>
          </Box>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            {dateTime(file.created_at)}
          </Typography>
        </Field>
        <Field label="Downloaded By">
          {downloads.length === 0
            ? "Not yet"
            : downloads.map((download, index) => (
                <Box key={`${download.at}-${index}`} sx={{ py: 0.25 }}>
                  {download.by || "-"}
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    {dateTime(download.at)}
                  </Typography>
                </Box>
              ))}
        </Field>
        <Box sx={{ gridColumn: "1 / -1" }}>
          <Field label="Shared With">
            {file.shared_with.length === 0
              ? "-"
              : file.shared_with.map((person) => (
                  <Box
                    key={person.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      py: 0.25,
                    }}
                  >
                    <UserAvatar person={person} size={22} />
                    <span>{person.name}</span>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                      {person.organization}
                    </Typography>
                  </Box>
                ))}
          </Field>
        </Box>
        {file.message ? (
          <Box sx={{ gridColumn: "1 / -1" }}>
            <Field label="Note">
              <Typography sx={{ whiteSpace: "pre-wrap", fontSize: 14 }}>
                {file.message}
              </Typography>
            </Field>
          </Box>
        ) : null}
      </Box>
      {staff && history.length > 0 ? (
        <>
          <Divider sx={{ my: 2 }} />
          <Field label="History">
            {history.map((event, index) => (
              <Box
                key={`${event.at}-${index}`}
                sx={{ display: "flex", gap: 1, fontSize: 13, py: 0.25 }}
              >
                <Box
                  component="span"
                  sx={{ minWidth: 150, color: "text.secondary" }}
                >
                  {dateTime(event.at)}
                </Box>
                <span>
                  {eventLine(event)}
                  {event.by ? ` by ${event.by}` : ""}
                </span>
              </Box>
            ))}
          </Field>
        </>
      ) : null}
    </AppDialog>
  );
}
